import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

interface HeaderProps {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
  isDarkMode: boolean;
  toggleTheme: () => void;
  isAuthenticated: boolean;
  userName: string;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({
  isMenuOpen,
  toggleMenu,
  closeMenu,
  isDarkMode,
  toggleTheme,
  isAuthenticated,
  userName,
  onLogout
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && closeMenu();
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isMenuOpen, closeMenu]);

  const navLinks = [
    { path: '/', label: 'Início' },
    { path: '/services', label: 'Serviços' },
    { path: '/projects', label: 'Projetos' },
    { path: '/companies', label: 'Sobre' }
  ];

  return (
    <header className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="logo">
          <span className="logo-mark">SN</span>
          <span className="logo-name">Samuel Neri</span>
        </Link>
        
        <nav id="main-navigation" aria-label="Navegação principal" className={`main-nav ${isMenuOpen ? 'open' : ''}`}>
          <ul className="nav-links">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link 
                  to={link.path}
                  className={location.pathname === link.path ? 'active' : ''}
                  aria-current={location.pathname === link.path ? 'page' : undefined}
                  onClick={closeMenu}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {isMenuOpen && <button className="nav-overlay" onClick={closeMenu} aria-label="Fechar menu" />}

        <div className="header-actions">
          {isAuthenticated && (
            <div className="session-actions">
              <span className="session-user">{userName}</span>
              <button
                className="logout-button"
                onClick={onLogout}
                type="button"
                aria-label="Sair"
              >
                <i className="fas fa-right-from-bracket"></i>
              </button>
            </div>
          )}

          <Link to="/contact" className="header-cta" onClick={closeMenu}>Vamos conversar</Link>

          <button 
            className="theme-toggle" 
            onClick={toggleTheme}
            aria-label={isDarkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}
          >
            <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
          </button>

          <button 
            className={`menu-toggle ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={isMenuOpen}
            aria-controls="main-navigation"
          >
            <span className="menu-icon"></span>
            <span className="menu-icon"></span>
            <span className="menu-icon"></span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
